// Kiểu dữ liệu hồ sơ khách hàng 360: phân loại, ảnh liệu trình, chữ ký điện tử (UI-first, lưu localStorage + IndexedDB)

export type Tier = 'Thường' | 'Bạc' | 'Vàng' | 'Kim cương';
export const TIERS: Tier[] = ['Thường', 'Bạc', 'Vàng', 'Kim cương'];

export interface TierRule {
  tier: Tier;
  /** Chi tiêu thực trả trong kỳ xét hạng (đ) */
  minSpend: number;
  benefit: string;
}

export type SegmentKey = 'lead' | 'new' | 'vip' | 'loyal' | 'potential' | 'need_care' | 'at_risk' | 'lost';

export interface Customer360Config {
  tierRules: TierRule[];
  /** Kỳ xét hạng / RFM (tháng gần nhất) */
  windowMonths: number;
  /** Khách mới: lần đến đầu tiên trong N ngày */
  newDays: number;
  /** Cần chăm sóc: không quay lại sau N ngày */
  careDays: number;
  /** Có nguy cơ rời bỏ: không quay lại sau N ngày */
  atRiskDays: number;
  /** Ngủ đông / đã rời bỏ: không quay lại sau N ngày */
  lostDays: number;
  /** Trung thành: số lần đến trong kỳ từ mức này */
  loyalVisits: number;
  /** Lần đến chưa ký xác nhận sau N ngày → báo quản lý */
  visitSignDays: number;
  /** Áp dụng ký xác nhận lần đến từ ngày (YYYY-MM-DD); lần đến trước đó không đòi chữ ký. Rỗng = mọi lần đến */
  visitSignSince: string;
}

export const DEFAULT_C360_CONFIG: Customer360Config = {
  tierRules: [
    { tier: 'Thường', minSpend: 0, benefit: 'Tích điểm, nhắc lịch tự động' },
    { tier: 'Bạc', minSpend: 10_000_000, benefit: 'Giảm 3% dịch vụ lẻ, quà sinh nhật' },
    { tier: 'Vàng', minSpend: 30_000_000, benefit: 'Giảm 5%, ưu tiên đặt lịch, 1 buổi chăm sóc da/quý' },
    { tier: 'Kim cương', minSpend: 80_000_000, benefit: 'Giảm 10%, chuyên viên riêng, phòng VIP' },
  ],
  windowMonths: 12,
  newDays: 30,
  careDays: 45,
  atRiskDays: 90,
  lostDays: 180,
  loyalVisits: 6,
  visitSignDays: 3,
  visitSignSince: '',
};

/** Phân loại tay (ghi đè kết quả tự động) */
export interface ClassOverride {
  tier?: Tier;
  segment?: SegmentKey;
  /** Ghi đè đến ngày (YYYY-MM-DD); rỗng = đến khi gỡ */
  until?: string;
  reason: string;
  by: string;
  at: string;
}

export interface ClassLog { at: string; by: string; text: string }

// ---------------------------------------------------------------------------
// Ảnh liệu trình

export type PhotoPhase = 'before' | 'during' | 'after';
export const PHASE_LABEL: Record<PhotoPhase, string> = { before: 'Trước', during: 'Trong liệu trình', after: 'Sau' };
export const PHASE_CLS: Record<PhotoPhase, string> = {
  before: 'bg-slate-700 text-white border-slate-700',
  during: 'bg-sky-600 text-white border-sky-600',
  after: 'bg-emerald-600 text-white border-emerald-600',
};
export const PHOTO_ANGLES = ['Chính diện', 'Nghiêng trái', 'Nghiêng phải', 'Cận cảnh', 'Vùng điều trị'];

export interface TreatmentPhoto {
  id: string;
  /** Mã khách CMS */
  customerKey: string;
  cardId?: string;
  usageId?: string;
  /** Buổi thứ mấy của thẻ */
  sessionNo?: number;
  phase: PhotoPhase;
  angle: string;
  note: string;
  takenAt: string;
  createdAt: string;
  by: string;
  /** Ảnh gốc (đã nén) và ảnh nhỏ trong IndexedDB */
  mediaId: string;
  thumbId: string;
  /** Ẩn khỏi bộ sưu tập (giữ lại để đối chiếu) */
  hidden?: { at: string; by: string; reason: string };
  /** Dữ liệu demo (xóa được bằng nút Xóa dữ liệu demo) */
  demo?: boolean;
}

// ---------------------------------------------------------------------------
// Chữ ký điện tử

/** session: chữ ký từng buổi (bản cũ) · visit: một phiếu cho cả lần đến, ký khi khách ra về */
export type SignKind = 'consent' | 'contract' | 'photo' | 'visit' | 'session';

export const SIGN_KIND: Record<SignKind, { label: string; short: string; desc: string }> = {
  consent: { label: 'Phiếu đồng ý thực hiện dịch vụ', short: 'Đồng ý điều trị', desc: 'Khách xác nhận đã được tư vấn, khai báo tiền sử và đồng ý thực hiện' },
  contract: { label: 'Xác nhận mua thẻ liệu trình', short: 'Hợp đồng thẻ', desc: 'Gói, giá, lịch thanh toán, hạn dùng và chính sách hoàn – gia hạn' },
  photo: { label: 'Đồng ý sử dụng hình ảnh', short: 'Đồng ý hình ảnh', desc: 'Cho phép chụp – lưu ảnh trước/sau để theo dõi liệu trình' },
  visit: { label: 'Phiếu xác nhận thực hiện dịch vụ', short: 'Xác nhận lần đến', desc: 'Khách ký một lần khi ra về cho mọi dịch vụ đã làm trong ngày' },
  session: { label: 'Xác nhận buổi thực hiện', short: 'Xác nhận buổi', desc: 'Chữ ký từng buổi (bản cũ)' },
};

// ---------------------------------------------------------------------------
// Nhật ký thay đổi hồ sơ (Lịch sử cập nhật)

export type AuditKind = 'profile' | 'class' | 'link' | 'treatment' | 'sign' | 'photo' | 'visit' | 'notify';

export const AUDIT_KIND: Record<AuditKind, { label: string; color: string }> = {
  profile: { label: 'Thông tin KH', color: '#3b82f6' },
  class: { label: 'Phân loại', color: '#8b5cf6' },
  link: { label: 'Liên kết', color: '#64748b' },
  treatment: { label: 'Liệu trình', color: '#3e91ab' },
  sign: { label: 'Chữ ký', color: '#16a34a' },
  photo: { label: 'Ảnh', color: '#0891b2' },
  visit: { label: 'Lần đến', color: '#d97706' },
  notify: { label: 'Thông báo', color: '#94a3b8' },
};

export interface AuditChange { label: string; from: string; to: string }

export interface AuditLog {
  id: string;
  at: string;
  by: string;
  kind: AuditKind;
  title: string;
  changes?: AuditChange[];
  note?: string;
  demo?: boolean;
}

/** Dữ liệu demo đã nạp cho một khách — để xóa trả về đúng như trước */
export interface DemoMeta {
  posId: string;
  /** Hồ sơ POS do demo tự liên kết (xóa demo thì bỏ liên kết) */
  linked: boolean;
  /** Mốc áp dụng ký lần đến trước khi nạp demo */
  prevSince: string;
  docIds: string[];
  photoIds: string[];
  recordIds: string[];
  /** Lượt trừ buổi đã chuyển sang "chưa ký" để minh họa chờ ký */
  usageIds: string[];
}

/** Thông tin thêm của một lần khách đến (khóa: mã khách POS + ngày) */
export interface VisitRecord {
  /** `${posCustomerId}|${YYYY-MM-DD}` */
  id: string;
  posCustomerId: string;
  date: string;
  /** Khách tự đánh giá 1–5 sao */
  rating?: number;
  ratingNote?: string;
  /** Khách ký trên phiếu giấy: các mục đã có phiếu, ảnh chụp phiếu (IndexedDB) */
  paper?: { at: string; by: string; keys: string[]; photoId?: string };
  /** Hẹn ký sau (khách vội, máy hỏng…) */
  deferred?: { at: string; by: string; reason: string };
  demo?: boolean;
}

export interface DocSection { heading: string; lines: string[] }

export interface SignDoc {
  id: string;
  kind: SignKind;
  title: string;
  /** Mã khách CMS (có thể trống với chữ ký buổi tạo từ màn trừ buổi) */
  customerKey?: string;
  posCustomerId?: string;
  cardId?: string;
  usageId?: string;
  /** Phiếu lần đến: ngày đến + các mục (u:mã lượt trừ, o:mã hóa đơn) khách đã ký xác nhận */
  visitDate?: string;
  coveredKeys?: string[];
  /** Nội dung chụp lại lúc ký — đổi mẫu sau này không ảnh hưởng văn bản đã ký */
  sections: DocSection[];
  signerName: string;
  signerPhone: string;
  /** Nhân viên chứng kiến */
  witness: string;
  /** Ảnh chữ ký (PNG) trong IndexedDB */
  signatureId: string;
  /** SHA-256 của nội dung + chữ ký, để phát hiện chỉnh sửa */
  hash: string;
  signedAt: string;
  device: string;
  branchId: string;
  status: 'signed' | 'revoked';
  revoked?: { at: string; by: string; reason: string };
  printCount: number;
  demo?: boolean;
}
